import { useEffect, useRef, lazy as reactLazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { DefneWidget } from "@/components/DefneWidget";
import LoginPage from "@/pages/LoginPage";
import { PageTracker } from "@/components/PageTracker";
import { ShareFab } from "@/components/ShareFab";
import { LangSwitcher, LangSuggestBar } from "@/components/LangSwitcher";
import { LANG, LANG_PREFIX, METHODS_PATH } from "@/lib/i18n";

// Yayın sonrası eski sekmelerde chunk dosyaları 404 olur → sonsuz "Yükleniyor…". Bir kez otomatik yenile.
const lazy = (factory) => reactLazy(() => factory().catch((err) => {
  const key = "chunk-reload-" + window.location.pathname;
  if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); window.location.reload(); return new Promise(() => {}); }
  sessionStorage.removeItem(key);
  throw err;
}));
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import { UpgradeModal } from "@/components/Upgrade";
import "@/App.css";

import { RouteSeo } from "@/lib/seo";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const OpportunitiesPage = lazy(() => import("@/pages/OpportunitiesPage"));
const TrackingPage = lazy(() => import("@/pages/TrackingPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const MethodsPage = lazy(() => import("@/pages/MethodsPage"));
const AutomationPage = lazy(() => import("@/pages/AutomationPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const FactoryPage = lazy(() => import("@/pages/FactoryPage"));
const CoursesPage = lazy(() => import("@/pages/CoursesPage"));
const ShortsPage = lazy(() => import("@/pages/ShortsPage"));
const MiniFactoriesPage = lazy(() => import("@/pages/MiniFactoriesPage"));
const PodPage = lazy(() => import("@/pages/PodPage"));
const SaasPage = lazy(() => import("@/pages/SaasPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const MembersPage = lazy(() => import("@/pages/MembersPage"));
const PublicMethodsPage = lazy(() => import("@/pages/PublicMethodsPage"));
const PublicMethodPage = lazy(() => import("@/pages/PublicMethodPage"));
const PublicBlogPage = lazy(() => import("@/pages/PublicBlogPage"));
const PublicGuidesPage = lazy(() => import("@/pages/PublicGuidesPage"));
const PublicToolsPage = lazy(() => import("@/pages/PublicToolsPage"));
const FreeRightsPage = lazy(() => import("@/pages/FreeRightsPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/PasswordResetPages").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/PasswordResetPages").then((m) => ({ default: m.ResetPasswordPage })));
const PublicGuidePage = lazy(() => import("@/pages/PublicGuidesPage").then((m) => ({ default: m.PublicGuidePage })));
const PublicBlogPostPage = lazy(() => import("@/pages/PublicBlogPostPage"));
const PublicPodcastPage = lazy(() => import("@/pages/PublicPodcastPage"));
const TermsPage = lazy(() => import("@/pages/LegalPages"));
const PrivacyPage = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const TrendsPage = lazy(() => import("@/pages/TrendsPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ToolPage = lazy(() => import("@/pages/ToolPage"));
const AffiliatePage = lazy(() => import("@/pages/AffiliatePage"));
const AffiliateOutreachPage = lazy(() => import("@/pages/AffiliateOutreachPage"));

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center" data-testid="auth-loading">
      <div className="h-10 w-10 rounded-full border-2 border-emerald-500/30 border-t-emerald-400 animate-spin" />
    </div>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sessionId })
      .then(({ data }) => { setUser(data); navigate("/", { replace: true, state: { user: data } }); })
      .catch(() => { setUser(false); navigate("/login", { replace: true }); });
  }, [setUser, navigate]);
  return <Splash />;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <LandingPage />;
  return <Layout><DashboardPage /></Layout>;
}

function PublicOrLayout({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100">
      <LangSuggestBar />
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5"><Link to="/" className="font-heading text-lg font-bold" data-testid="public-home-link">Fırsat Avcısı</Link><div className="flex items-center gap-3"><LangSwitcher /><Link to="/login" className="rounded-full border border-white/15 px-4 py-1.5 text-sm hover:bg-white/5" data-testid="public-login-link">Giriş</Link></div></header>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 pb-16">{children}</main>
    </div>
  );
  return <Layout>{children}</Layout>;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (<>
    <RouteSeo />
    <PageTracker />
    <ShareFab />
    <DefneWidget />
    <Suspense fallback={<div className="p-8 text-sm text-muted-foreground" data-testid="page-loading">Yükleniyor…</div>}>
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<Home />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/methods" element={<Protected><MethodsPage /></Protected>} />
      <Route path="/automation" element={<Protected><AutomationPage /></Protected>} />
      <Route path="/marketing" element={<Protected><MarketingPage /></Protected>} />
      <Route path="/factory" element={<Protected><FactoryPage /></Protected>} />
      <Route path="/courses" element={<Protected><CoursesPage /></Protected>} />
      <Route path="/shorts" element={<Protected><ShortsPage /></Protected>} />
      <Route path="/mini" element={<Protected><MiniFactoriesPage /></Protected>} />
      <Route path="/pod" element={<Protected><PodPage /></Protected>} />
      <Route path="/saas" element={<Protected><SaasPage /></Protected>} />
      <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
      <Route path="/members" element={<Protected><MembersPage /></Protected>} />
      <Route path="/trends" element={<Protected><TrendsPage /></Protected>} />
      <Route path="/tools/:slug" element={<ToolPage />} />
      <Route path={METHODS_PATH} element={<PublicMethodsPage />} />
      <Route path={`${METHODS_PATH}/:slug`} element={<PublicMethodPage />} />
      <Route path="/rehber" element={<PublicGuidesPage />} />
      <Route path="/araclar" element={<PublicToolsPage />} />
      <Route path="/ucretsiz-haklar" element={<FreeRightsPage />} />
      <Route path="/rehber/:slug" element={<PublicGuidePage />} />
      <Route path="/blog" element={<PublicBlogPage />} />
      <Route path="/blog/:slug" element={<PublicBlogPostPage />} />
      <Route path="/podcast" element={<PublicPodcastPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      {LANG !== "tr" && <Route path="/yontemler" element={<Navigate to={METHODS_PATH} replace />} />}
      {LANG !== "tr" && <Route path="/yontemler/:slug" element={<PublicMethodPage />} />}
      <Route path="/affiliate" element={<Protected><AffiliatePage /></Protected>} />
      <Route path="/affiliate-outreach" element={<Protected><AffiliateOutreachPage /></Protected>} />
      <Route path="/pricing" element={<PublicOrLayout><PricingPage /></PublicOrLayout>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
      <Route path="/opportunities" element={<Protected><OpportunitiesPage /></Protected>} />
      <Route path="/tracking" element={<Protected><TrackingPage /></Protected>} />
      <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  </>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={LANG_PREFIX}>
      <AuthProvider>
        <AppRouter />
        <UpgradeModal />
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
